import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { slugify, type Draft, type Entry } from "./types";

export function useAdminChangelog(opts: { onSaved?: () => void; onDeleted?: () => void } = {}) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["admin-changelog-entries"] });
    qc.invalidateQueries({ queryKey: ["changelog-entries"] });
    qc.invalidateQueries({ queryKey: ["changelog-unread"] });
  };

  const { data: entries, isLoading } = useQuery({
    queryKey: ["admin-changelog-entries"],
    queryFn: async (): Promise<Entry[]> => {
      const { data, error } = await supabase
        .from("changelog_entries")
        .select("*")
        .order("published_at", { ascending: false, nullsFirst: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Entry[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (d: Draft) => {
      if (!user) throw new Error("Not signed in");
      const trimmedSlug = d.slug.trim() || slugify(d.title);
      if (!/^[a-z0-9][a-z0-9-]{1,80}$/.test(trimmedSlug)) {
        throw new Error("Slug must be lowercase letters, numbers, and dashes (2–81 chars).");
      }
      if (!d.title.trim() || !d.summary.trim()) {
        throw new Error("Title and summary are required.");
      }
      const payload = {
        slug: trimmedSlug,
        title: d.title.trim(),
        summary: d.summary.trim(),
        body: d.body,
        category: d.category,
        published_at: d.published ? new Date().toISOString() : null as string | null,
      };
      if (d.id) {
        const existing = entries?.find((e) => e.id === d.id);
        if (d.published && existing?.published_at) {
          payload.published_at = existing.published_at;
        }
        const { error } = await supabase.from("changelog_entries").update(payload).eq("id", d.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("changelog_entries")
          .insert({ ...payload, created_by: user.id });
        if (error) throw error;
      }
      return d;
    },
    onSuccess: (d) => {
      invalidateAll();
      toast.success(d.id ? "Entry updated" : "Entry created");
      opts.onSaved?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePublishMutation = useMutation({
    mutationFn: async (entry: Entry) => {
      const newVal = entry.published_at ? null : new Date().toISOString();
      const { error } = await supabase
        .from("changelog_entries")
        .update({ published_at: newVal })
        .eq("id", entry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast.success("Visibility updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("changelog_entries").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast.success("Entry deleted");
      opts.onDeleted?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return { entries, isLoading, saveMutation, togglePublishMutation, deleteMutation };
}
